"use client";
import { Tag, type TagTone } from "@/components/ui/Tag";
import { dashboardContent, fmt } from "@/lib/content";
import { fmtScore, shortHotkey } from "@/lib/dashboard/format";
import { isWorking } from "@/lib/dashboard/screener";
import type { MinerPosition } from "@/lib/dashboard/table";

const STATUS_TONE: Record<string, TagTone> = {
  scored: "ok",
  "not qualified": "accent",
  "failed review": "solid",
};

/**
 * The followed miner, above the table: where it stands, and a way to get to its row
 * without paging through a hundred others. Shown for every competition, including the
 * ones it did not enter - saying so is the answer to the question someone opened it with.
 */
export function MyMinerStrip({
  hotkey,
  position,
  onShow,
  onForget,
}: {
  hotkey: string;
  position: MinerPosition | null;
  onShow: () => void;
  onForget: () => void;
}) {
  const t = dashboardContent.myMiner;
  const statusLabels = dashboardContent.leaderboard.statusLabels;
  const overallLabel = dashboardContent.leaderboard.columns.overall;
  const status = position?.row.status ?? null;

  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-md border border-accent-line bg-accent-soft/40 px-4 py-2.5 text-[13px]">
      <span className="eyebrow text-[10.5px] text-accent">{t.label}</span>
      <span className="font-mono text-[12px]">{shortHotkey(hotkey, 6)}</span>
      {position ? (
        <>
          {position.place !== null && (
            <>
              <span className="tabular-nums text-fg-2">{fmt(t.place, { place: position.place, total: position.total })}</span>
              <span className="text-fg-muted">
                {overallLabel} <span className="tabular-nums font-medium text-fg">{fmtScore(position.row.overall)}</span>
              </span>
            </>
          )}
          {status && (
            <Tag tone={STATUS_TONE[status] ?? "muted"} size="xs" busy={isWorking(status)}>
              {statusLabels[status] ?? status}
            </Tag>
          )}
        </>
      ) : (
        <span className="text-fg-muted">{t.absent}</span>
      )}
      <span className="ml-auto flex items-center gap-4">
        {position && (
          <button type="button" onClick={onShow} className="cursor-pointer text-fg transition-colors hover:text-accent">
            {t.show} <span aria-hidden>&darr;</span>
          </button>
        )}
        <button type="button" onClick={onForget} className="cursor-pointer text-fg-muted transition-colors hover:text-fg">
          {t.forget}
        </button>
      </span>
    </div>
  );
}
